import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Edit, MapPin, Phone, Users, Home, X, Map as MapIcon, Grid3x3 } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { LocationPicker } from "@/components/LocationPicker";
import { FitBounds } from "@/components/FitBounds";
import { useMemo } from "react";
import { Link } from "react-router-dom";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const CURRENT_YEAR = new Date().getFullYear();

function emptyVillage() {
  return {
    name: "",
    household_count: 0,
    latitude: 39.9334,
    longitude: 32.8597,
    muhtar_name: "",
    muhtar_phone: "",
    azalar: [],
    populations: [{ year: CURRENT_YEAR, population: 0 }],
    notes: "",
  };
}

export default function VillagesPage() {
  const [villages, setVillages] = useState([]);
  const [view, setView] = useState("grid"); // grid | map
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVillage());
  const [search, setSearch] = useState("");

  const load = () => api.get("/villages").then((r) => setVillages(r.data));

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (!form.name.trim()) {
        toast.error("Köy adı zorunludur");
        return;
      }
      if (editing) {
        await api.put(`/villages/${editing.id}`, form);
        toast.success("Köy güncellendi");
      } else {
        await api.post("/villages", form);
        toast.success("Köy oluşturuldu");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyVillage());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata oluştu");
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`"${v.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/villages/${v.id}`);
      toast.success("Köy silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name,
      household_count: v.household_count,
      latitude: v.latitude,
      longitude: v.longitude,
      muhtar_name: v.muhtar_name,
      muhtar_phone: v.muhtar_phone,
      azalar: v.azalar || [],
      populations: v.populations?.length ? v.populations : [{ year: CURRENT_YEAR, population: 0 }],
      notes: v.notes || "",
    });
    setOpen(true);
  };

  const addAza = () => setForm({ ...form, azalar: [...form.azalar, { full_name: "", phone: "" }] });
  const removeAza = (i) => setForm({ ...form, azalar: form.azalar.filter((_, idx) => idx !== i) });
  const updateAza = (i, field, val) => {
    const n = [...form.azalar];
    n[i] = { ...n[i], [field]: val };
    setForm({ ...form, azalar: n });
  };

  const addPop = () => setForm({ ...form, populations: [...form.populations, { year: CURRENT_YEAR, population: 0 }] });
  const removePop = (i) => setForm({ ...form, populations: form.populations.filter((_, idx) => idx !== i) });
  const updatePop = (i, field, val) => {
    const n = [...form.populations];
    n[i] = { ...n[i], [field]: Number(val) || 0 };
    setForm({ ...form, populations: n });
  };

  const filtered = villages.filter((v) =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.muhtar_name || "").toLowerCase().includes(search.toLowerCase())
  );

  const mapPoints = useMemo(
    () => filtered.filter((v) => v.latitude && v.longitude).map((v) => [v.latitude, v.longitude]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [villages, search]
  );

  const latestPop = (v) => {
    if (!v.populations?.length) return 0;
    return [...v.populations].sort((a, b) => b.year - a.year)[0].population;
  };

  return (
    <AppLayout
      title="Köyler"
      subtitle="Köy kartları, muhtar bilgileri ve harita görünümü"
      actions={
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyVillage()); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-village-button" className="bg-emerald-700 hover:bg-emerald-800">
              <Plus className="h-4 w-4 mr-1.5" /> Yeni Köy
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="village-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? "Köyü Düzenle" : "Yeni Köy Ekle"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Köy Adı *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="village-name-input" />
              </div>
              <div>
                <Label>Hane Sayısı</Label>
                <Input type="number" value={form.household_count}
                       onChange={(e) => setForm({ ...form, household_count: Number(e.target.value) || 0 })}
                       data-testid="village-household-input" />
              </div>
              <div></div>
              <div>
                <Label>Enlem (Latitude)</Label>
                <Input type="number" step="0.000001" value={form.latitude}
                       onChange={(e) => setForm({ ...form, latitude: Number(e.target.value) || 0 })}
                       data-testid="village-latitude-input" />
              </div>
              <div>
                <Label>Boylam (Longitude)</Label>
                <Input type="number" step="0.000001" value={form.longitude}
                       onChange={(e) => setForm({ ...form, longitude: Number(e.target.value) || 0 })}
                       data-testid="village-longitude-input" />
              </div>
              <div className="col-span-2">
                <div className="text-xs text-slate-500 mb-1.5">Konumu seçmek için harita üzerinde köyün yerine tıklayın.</div>
                {open && (
                  <LocationPicker lat={form.latitude} lng={form.longitude}
                                  onPick={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))} />
                )}
              </div>
              <div>
                <Label>Muhtar Adı Soyadı</Label>
                <Input value={form.muhtar_name} onChange={(e) => setForm({ ...form, muhtar_name: e.target.value })}
                       data-testid="village-muhtar-name-input" />
              </div>
              <div>
                <Label>Muhtar Telefonu</Label>
                <Input value={form.muhtar_phone} onChange={(e) => setForm({ ...form, muhtar_phone: e.target.value })}
                       data-testid="village-muhtar-phone-input" />
              </div>

              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Yıllık Nüfus</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addPop}
                          data-testid="add-population-button">
                    <Plus className="h-3 w-3 mr-1" /> Yıl Ekle
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.populations.map((p, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input type="number" placeholder="Yıl" value={p.year}
                             onChange={(e) => updatePop(i, "year", e.target.value)}
                             className="w-32" />
                      <Input type="number" placeholder="Nüfus" value={p.population}
                             onChange={(e) => updatePop(i, "population", e.target.value)}
                             className="flex-1" />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removePop(i)}>
                        <X className="h-4 w-4 text-slate-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Köy Azaları</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addAza}
                          data-testid="add-aza-button">
                    <Plus className="h-3 w-3 mr-1" /> Aza Ekle
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.azalar.map((a, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input placeholder="Ad Soyad" value={a.full_name}
                             onChange={(e) => updateAza(i, "full_name", e.target.value)}
                             className="flex-1" />
                      <Input placeholder="Telefon" value={a.phone}
                             onChange={(e) => updateAza(i, "phone", e.target.value)}
                             className="flex-1" />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeAza(i)}>
                        <X className="h-4 w-4 text-slate-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-span-2">
                <Label>Notlar</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={handleSave} className="bg-emerald-700 hover:bg-emerald-800"
                      data-testid="village-submit-button">
                {editing ? "Güncelle" : "Oluştur"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <Input
          placeholder="Köy veya muhtar ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
          data-testid="village-search-input"
        />
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="village-view-tabs">
            <TabsTrigger value="grid" data-testid="village-view-grid"><Grid3x3 className="h-4 w-4 mr-1.5" /> Kart</TabsTrigger>
            <TabsTrigger value="map" data-testid="village-view-map"><MapIcon className="h-4 w-4 mr-1.5" /> Harita</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((v) => (
            <Card key={v.id} className="p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`village-card-${v.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <Link to={`/koyler/${v.id}`} className="font-display font-semibold text-lg text-slate-900 hover:text-emerald-700 transition-colors"
                        data-testid={`village-detail-link-${v.id}`}>{v.name}</Link>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                    <MapPin className="h-3 w-3" /> {Number(v.latitude || 0).toFixed(4)}, {Number(v.longitude || 0).toFixed(4)}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(v)}
                          data-testid={`edit-village-${v.id}`}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(v)}
                          data-testid={`delete-village-${v.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="p-3 bg-slate-50 rounded">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Users className="h-3 w-3" /> Nüfus
                  </div>
                  <div className="font-mono font-semibold text-slate-900 mt-1">{latestPop(v).toLocaleString("tr-TR")}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Home className="h-3 w-3" /> Hane
                  </div>
                  <div className="font-mono font-semibold text-slate-900 mt-1">{v.household_count}</div>
                </div>
              </div>
              {v.muhtar_name && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-xs text-slate-500">Muhtar</div>
                  <div className="font-medium text-slate-900 text-sm mt-0.5">{v.muhtar_name}</div>
                  {v.muhtar_phone && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                      <Phone className="h-3 w-3" /> {v.muhtar_phone}
                    </div>
                  )}
                </div>
              )}
              {v.azalar?.length > 0 && (
                <div className="mt-2 text-xs text-slate-500">
                  {v.azalar.length} aza kayıtlı
                </div>
              )}
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-500" data-testid="no-villages-message">
              Henüz köy eklenmedi. Yeni köy eklemek için üstteki butona tıklayın.
            </div>
          )}
        </div>
      ) : (
        <Card className="overflow-hidden border border-slate-200 shadow-sm" data-testid="village-map-view">
          <div className="h-[600px]">
            <MapContainer
              center={filtered.length ? [filtered[0].latitude, filtered[0].longitude] : [39.9334, 32.8597]}
              zoom={filtered.length > 1 ? 8 : 11}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds points={mapPoints} />
              {filtered.filter(v => v.latitude && v.longitude).map((v) => (
                <Marker key={v.id} position={[v.latitude, v.longitude]}>
                  <Popup>
                    <div className="font-semibold text-slate-900">{v.name}</div>
                    <Link to={`/koyler/${v.id}`} className="text-xs text-emerald-700 underline">Detayları gör</Link>
                    <div className="text-xs text-slate-600 mt-1">
                      Nüfus: {latestPop(v).toLocaleString("tr-TR")} • Hane: {v.household_count}
                    </div>
                    {v.muhtar_name && <div className="text-xs mt-1">Muhtar: {v.muhtar_name}{v.muhtar_phone ? ` (${v.muhtar_phone})` : ""}</div>}
                    {v.azalar?.length > 0 && <div className="text-xs mt-1">Aza: {v.azalar.map((a) => a.full_name).join(", ")}</div>}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </Card>
      )}
    </AppLayout>
  );
}
